import { z } from 'zod';
import { dashboardWidgetIds } from '../dashboard-widgets.js';
import {
  languages,
  patientSexes,
  referralSources,
  themes,
  type AppointmentKind,
  type AppointmentSource,
  type AppointmentStatus,
  type Language,
  type PatientSex,
  type ReferralSource,
  type Theme,
} from '../enums.js';

/**
 * Contrat réseau du tableau de bord. Tout y est agrégé côté serveur, en centimes entiers pour
 * l'argent, et aucune série n'est fabriquée : la V1 tirait son graphique d'activité d'un
 * `Math.random()` et sa tendance de revenu de multiplicateurs en dur (docs/audit §4.4).
 *
 * Aucun libellé humain ne circule ici. Les indications portent un `code` et le front affiche
 * `dashboard.insights.<code>`.
 */

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/** `mine=1` restreint l'aperçu aux rendez-vous et brouillons du praticien appelant. */
export const dashboardTodayQuerySchema = z
  .object({
    /** Jour civil du cabinet (Europe/Luxembourg). Absent : aujourd'hui. */
    date: z.iso.date().optional(),
    mine: z
      .enum(['0', '1'])
      .optional()
      .transform((value) => value === '1'),
  })
  .strict();
export type DashboardTodayQuery = z.infer<typeof dashboardTodayQuerySchema>;

export const dashboardChartsQuerySchema = z
  .object({ months: z.coerce.number().int().min(3).max(24).default(12) })
  .strict();
export type DashboardChartsQuery = z.infer<typeof dashboardChartsQuerySchema>;

/**
 * Disposition du tableau de bord. Le blob est possédé par le client, mais son contenu est
 * validé — un identifiant hors de la liste fermée est refusé en 422, sans quoi une disposition
 * enregistrée survivrait à la suppression du widget qu'elle nomme.
 *
 * Deux formes cohabitent. L'ancienne est un tableau de widgets : les sections viennent alors du
 * registre. La forme actuelle est un document `{ items }` qui mêle en-têtes de section et widgets,
 * dans l'ordre d'affichage, pour que renommer ou déplacer une section survive à la déconnexion.
 */
export const dashboardWidgetLayoutSchema = z
  .object({
    widgetId: z.enum(dashboardWidgetIds),
    hidden: z.boolean(),
    width: z.int().min(1).max(4),
  })
  .strict();
export type DashboardWidgetLayout = z.infer<typeof dashboardWidgetLayoutSchema>;

/** Plafond des sections ajoutées par l'utilisateur, en plus de celles du registre. */
export const dashboardMaxSections = 24;

const dashboardSectionIdSchema = z.string().regex(/^section-[a-z0-9-]{1,48}$/);

export const dashboardSectionItemSchema = z
  .object({
    kind: z.literal('section'),
    id: dashboardSectionIdSchema,
    title: z.string().max(80),
  })
  .strict();

export const dashboardWidgetItemSchema = z
  .object({
    kind: z.literal('widget'),
    widgetId: z.enum(dashboardWidgetIds),
    hidden: z.boolean(),
    width: z.int().min(1).max(4),
  })
  .strict();

const dashboardLayoutListSchema = z
  .array(dashboardWidgetLayoutSchema)
  .max(dashboardWidgetIds.length)
  .refine(
    (widgets) => new Set(widgets.map((widget) => widget.widgetId)).size === widgets.length,
    'Un widget ne peut apparaître qu’une fois',
  );

export const dashboardLayoutDocumentSchema = z
  .object({
    items: z
      .array(z.discriminatedUnion('kind', [dashboardSectionItemSchema, dashboardWidgetItemSchema]))
      .max(dashboardWidgetIds.length + dashboardMaxSections),
  })
  .strict()
  .refine((document) => {
    const widgets = document.items.filter((item) => item.kind === 'widget');
    const sections = document.items.filter((item) => item.kind === 'section');
    return (
      new Set(widgets.map((item) => item.widgetId)).size === widgets.length &&
      new Set(sections.map((item) => item.id)).size === sections.length
    );
  }, 'Un widget ou une section ne peut apparaître qu’une fois');

export const dashboardLayoutSchema = z.union([
  dashboardLayoutListSchema,
  dashboardLayoutDocumentSchema,
]);
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;
export type DashboardLayoutDocument = z.infer<typeof dashboardLayoutDocumentSchema>;

/** `PUT` de remplacement complet, comme les réglages de facturation : un champ absent serait ambigu. */
export const userPreferencesUpdateSchema = z
  .object({
    theme: z.enum(themes),
    language: z.enum(languages),
    dashboardLayout: dashboardLayoutSchema.nullable(),
  })
  .strict();
export type UserPreferencesUpdateInput = z.infer<typeof userPreferencesUpdateSchema>;

/* -------------------------------------------------------------------------- */
/* Résumé                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Valeur du mois courant, du mois précédent, et variation en pourcentage entier.
 * `changePercent` est nul quand le mois précédent l'est : diviser par zéro n'a pas de sens, et la
 * V1 affichait alors « +100 % » pour un cabinet qui démarrait.
 */
export interface DashboardMonthlyComparisonDto {
  current: number;
  previous: number;
  changePercent: number | null;
}

export interface DashboardSummaryDto {
  /** Dossiers ni archivés, ni en corbeille, ni en attente de validation. */
  activePatients: number;
  newPatients: DashboardMonthlyComparisonDto;
  completedEncounters: DashboardMonthlyComparisonDto;
  /** Rendez-vous `pending` ou `confirmed` dans les sept jours à venir. */
  upcomingAppointments: number;
  /** Paiements constatés dans le mois civil courant, en centimes. */
  collectedThisMonthCents: number;
  /** Factures émises ou partiellement payées : total − payé − crédité. */
  outstandingCents: number;
  /** Même calcul, limité aux factures dont l'échéance est passée. */
  overdueCents: number;
  pendingDraftEncounters: number;
  pendingDunningNotices: number;
}

/* -------------------------------------------------------------------------- */
/* Aperçu du jour                                                              */
/* -------------------------------------------------------------------------- */

/** Le minimum pour dessiner une ligne de l'aperçu. Le dossier complet a sa propre route. */
export interface DashboardPatientSummaryDto {
  id: string;
  firstName: string;
  lastName: string;
}

export interface DashboardTodayAppointmentDto {
  id: string;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  source: AppointmentSource;
  /**
   * `note` : post-it de l'agenda. L'aperçu du jour ne les renvoie pas ; le champ reste pour que
   * l'écran les écarte si une réponse en contient un. Le titre et le texte ne circulent pas ici.
   */
  kind: AppointmentKind;
  practitionerMemberId: string;
  consultationTypeId: string | null;
  consultationTypeName: string | null;
  /** Nul pour une réservation publique pas encore rattachée ; l'identité est alors dans le snapshot. */
  patient: DashboardPatientSummaryDto | null;
  patientSnapshotName: string | null;
}

export interface DashboardTodayDraftDto {
  id: string;
  startedAt: string;
  patient: DashboardPatientSummaryDto;
}

export interface DashboardTodayDto {
  /** Jour civil du cabinet effectivement rendu. */
  date: string;
  appointments: DashboardTodayAppointmentDto[];
  /** Premier rendez-vous encore à venir parmi ceux du jour, sinon nul. */
  nextAppointment: DashboardTodayAppointmentDto | null;
  completedCount: number;
  /** `pending` et `confirmed` encore à honorer. Les annulés ne comptent nulle part. */
  remainingCount: number;
  noShowCount: number;
  draftEncounters: DashboardTodayDraftDto[];
}

/* -------------------------------------------------------------------------- */
/* Graphiques                                                                  */
/* -------------------------------------------------------------------------- */

/** `month` au format `YYYY-MM`, mois civil du cabinet. Les mois vides valent zéro, jamais absents. */
export interface DashboardMonthlyPointDto {
  month: string;
  value: number;
}

export const dashboardAgeBands = ['0-17', '18-34', '35-49', '50-64', '65+'] as const;
export type DashboardAgeBand = (typeof dashboardAgeBands)[number];

/** `unknown` : la colonne est nulle en base. Ce n'est pas une valeur d'enum, c'est son absence. */
export type DashboardSexBucket = PatientSex | 'unknown';
export type DashboardReferralBucket = ReferralSource | 'unknown';

export const dashboardSexBuckets = [...patientSexes, 'unknown'] as const;
export const dashboardReferralBuckets = [...referralSources, 'unknown'] as const;

export interface DashboardCountByStatusDto {
  status: AppointmentStatus;
  count: number;
}

export interface DashboardCountBySexDto {
  sex: DashboardSexBucket;
  count: number;
}

export interface DashboardCountByAgeBandDto {
  band: DashboardAgeBand;
  count: number;
}

export interface DashboardCountByReferralDto {
  source: DashboardReferralBucket;
  count: number;
}

export interface DashboardConsultationReasonCountDto {
  consultationReasonId: string;
  label: string;
  count: number;
}

export interface DashboardChartsDto {
  /** Bornes de la période, en mois civils inclus. */
  fromMonth: string;
  toMonth: string;
  completedEncounters: DashboardMonthlyPointDto[];
  newPatients: DashboardMonthlyPointDto[];
  collectedCents: DashboardMonthlyPointDto[];
  invoicedCents: DashboardMonthlyPointDto[];
  appointmentsByStatus: DashboardCountByStatusDto[];
  patientsBySex: DashboardCountBySexDto[];
  patientsByAgeBand: DashboardCountByAgeBandDto[];
  topConsultationReasons: DashboardConsultationReasonCountDto[];
  referralSources: DashboardCountByReferralDto[];
}

/* -------------------------------------------------------------------------- */
/* Indications                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Règles explicites, jamais un modèle : chaque code correspond à une requête dont la condition est
 * écrite dans le service. La V1 appelait cette carte « Insights IA » et affichait
 * « Bientôt disponible ».
 */
export const dashboardInsightCodes = [
  /** Factures échues depuis plus de trente jours. */
  'overdue_invoices_30d',
  /** Brouillons de consultation ouverts depuis plus de sept jours. */
  'stale_draft_encounters',
  /** Dossiers d'auto-inscription en attente de validation. */
  'temporary_patients',
  /** Rendez-vous `pending` dans les quarante-huit heures. */
  'pending_appointments_48h',
  /** Patients suivis au moins une fois et sans rendez-vous depuis plus de six mois. */
  'dormant_patients_6m',
  /** Anniversaires des sept jours à venir. */
  'birthdays_this_week',
] as const;
export type DashboardInsightCode = (typeof dashboardInsightCodes)[number];

export const dashboardInsightSeverities = ['info', 'warning'] as const;
export type DashboardInsightSeverity = (typeof dashboardInsightSeverities)[number];

export interface DashboardInsightDto {
  code: DashboardInsightCode;
  severity: DashboardInsightSeverity;
  /** Nombre exact de lignes concernées, même quand `ids` est tronqué. */
  count: number;
  /** Identifiants de la ressource visée par la règle, vingt au plus. */
  ids: string[];
}

export interface DashboardInsightsDto {
  insights: DashboardInsightDto[];
}

/* -------------------------------------------------------------------------- */
/* Opérationnel — remplissage, durée, réguliers                                */
/* -------------------------------------------------------------------------- */

/**
 * Trois indicateurs que la V1 calculait dans `DashboardAllStats` / `storage.ts`. Définitions
 * retenues (simplifiées, fidèles) :
 * — **taux de remplissage** : rendez-vous non annulés du mois civil ÷ (jours où une règle de
 *   disponibilité active tombe × 8 créneaux), en pourcentage entier ; 0 sans créneau estimé ;
 * — **durée moyenne** : moyenne arrondie du nombre de consultations (encounters) par patient qui
 *   en a au moins une — la V1 affichait « séances », pas des minutes ;
 * — **patients réguliers** : part des dossiers actifs ayant ≥ 2 consultations sur six mois.
 */
export interface DashboardOperationsDto {
  /** Pourcentage entier 0–… ; peut dépasser 100 si plus de rendez-vous que de créneaux estimés. */
  calendarFillRatePercent: number;
  /** Moyenne de séances par patient suivi, arrondie ; 0 s'il n'y a aucune consultation. */
  averageTreatmentSessions: number;
  /** Pourcentage entier ; 0 s'il n'y a aucun dossier actif. */
  regularPatientsRatePercent: number;
}

/* -------------------------------------------------------------------------- */
/* Pédiatrie                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Indicateurs pédiatriques. La V1 en codait deux sur trois en dur (indice céphalique nul, bilans du
 * mois à zéro) ; ici les trois se calculent sur les bilans du cabinet.
 */
export interface DashboardPediatricDto {
  /** Dossiers actifs de moins de douze mois aujourd'hui : la règle qui ouvre le bilan pédiatrique. */
  infantPatients: number;
  /** Bilans portés par une consultation du mois civil courant, brouillon ou finalisée. */
  assessmentsThisMonth: number;
  /**
   * Moyenne des indices céphaliques des bilans du mois qui portent largeur et longueur, à deux
   * décimales. Nulle quand aucun n'est mesuré.
   */
  averageCephalicIndex: number | null;
  /** Faux quand le cabinet n'a ni nourrisson ni aucun bilan : le widget montre son état vide. */
  hasPediatricActivity: boolean;
}

/* -------------------------------------------------------------------------- */
/* Préférences                                                                 */
/* -------------------------------------------------------------------------- */

export interface UserPreferencesDto {
  theme: Theme;
  language: Language;
  /** Nul tant que l'utilisateur n'a rien réorganisé : le front applique alors ses valeurs par défaut. */
  dashboardLayout: DashboardLayout | null;
}
